import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import { RFC } from 'state/StateTypes';
import { getUser, updateUser } from 'state/user/UserActions';
import { User, AdminStatus } from 'state/user/UserTypes';
import isAuthenticated from 'hooks/isAuthenticated';
import ViewContainer from 'components/ViewContainer';
import BackButton from 'components/BackButton';
import Loading from 'components/Loading';
import { InfoList, Info } from 'components/InfoList';
import Label from 'components/Label';
import ErrorMessage from 'components/ErrorMessage';
import styled from 'styled-components';
import DetailHeader from 'components/DetailHeader';
import { Error, ErrorCodes } from 'types';
import { deriveDateFromMongoId, convertTimeStamp, presentErrorBasedOnCode } from 'utils';
import SmallLoader from 'components/SmallLoader';
import ToggleButton from 'components/AdminToggleButton';
import { SET_IS_VIEWING_USER_DETAILS } from 'state/users/UsersActionTypes';
import { useDispatch } from 'react-redux';

interface Props extends RouteComponentProps {
   id?: string;
}
const AdminDetail: RFC<Props> = ({ id }) => {
   const [admin, setAdmin] = useState<User | undefined>(undefined);
   const [loading, setLoading] = useState<boolean>(true);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [error, setError] = useState<Error | null>(null);
   const [togglingStatus, setTogglingStatus] = useState<boolean>(false);
   const dispatch = useDispatch();

   useEffect(() => {
      (async () => {
         if (authenticated && id) {
            try {
               const admin = await getUser(id);
               setAdmin(admin);
               dispatch({ type: SET_IS_VIEWING_USER_DETAILS, payload: true });
            } catch (error) {
               //if error results from missing token it will not have a json method
               error = error.json ? await error.json() : error;
               setError(error);
               dispatch({ type: SET_IS_VIEWING_USER_DETAILS, payload: false });
            }
            setLoading(false);
         }
      })();
   }, [id]);

   const toggleAdminStatus = async (): Promise<void> => {
      try {
         await setTogglingStatus(true);
         const updatedAdmin = await updateUser({
            adminStatus: admin?.adminStatus === AdminStatus.active ? AdminStatus.disabled : AdminStatus.active
         }, admin!._id)
         setAdmin(updatedAdmin)
      } catch (error) {
         error = error.json ? await error.json() : error;
         console.log(error);
         setError(error);
      }
      setTogglingStatus(false);
   }

   return (
      <ViewContainer>
         <BackButton to="/admins" />
         {admin && <DetailHeader title="admin detail" loading={loading} />}
         {loading ? (
            <Loading />
         ) : error || !admin ? (
            <ErrorMessage>{error ? presentErrorBasedOnCode(error.code as ErrorCodes) : "Something went wrong in retrieving that admin's details!"}</ErrorMessage>
         ) : (
            <DetailList>
               <div>
                  <ToggleButton onClick={toggleAdminStatus}>
                     {admin.adminStatus === AdminStatus.active ? (
                        togglingStatus ? (
                           <SmallLoader />
                        ) : (
                           'Disable'
                        )
                     ) : togglingStatus ? (
                        <SmallLoader />
                     ) : (
                        'Enable'
                     )}
                  </ToggleButton>
               </div>
               <Info>
                  <Label>Name</Label> {admin.firstName} {admin.lastName}
               </Info>
               <Info>
                  <Label>Email</Label> {admin.email}
               </Info>
               <Info>
                  <Label>Phone</Label> {admin.phone}
               </Info>
               <Info>
                  <Label>Created</Label> {deriveDateFromMongoId(admin._id)}
               </Info>
               <Info>
                  <Label>Updated</Label> {admin.updatedAt ? convertTimeStamp(admin.updatedAt) : ''}
               </Info>
            </DetailList>
         )}
      </ViewContainer>
   );
};

export default AdminDetail;

const DetailList = styled(InfoList)`
   margin: unset;
   padding-left: 10px;
`;